import { JRCButton } from 'components/JRCButton/JRCButton';
import { WidgetLegacyMixedBoolean, WidgetQuickSurveyContentArr, WidgetQuickSurveyResponses } from 'jamespot-user-api';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { JRCModal } from '../../JRCModal/templates/JRCModal';

import { Typography } from '../../JRCTypography/JRCTypography.styles';

import { JRCWidgetQuickSurveyImage } from './JRCWidgetQuickSurveyImage';

import { JRCWidgetQuickSurveyResponse } from './JRCWidgetQuickSurveyResponse';

type JRCWidgetQuickSurveyProps = {
    limit: number;
    values: WidgetQuickSurveyContentArr[];
    userResponses?: WidgetQuickSurveyResponses;
    canCloseSurvey: WidgetLegacyMixedBoolean;
    isClosed?: WidgetLegacyMixedBoolean;
    open?: WidgetLegacyMixedBoolean;
    userHasAlreadyVoted?: WidgetLegacyMixedBoolean;
    anonymize?: WidgetLegacyMixedBoolean;
    seeResponseAfter?: WidgetLegacyMixedBoolean;
    canUpdate?: WidgetLegacyMixedBoolean;
    handleClickFile?: (entry: WidgetQuickSurveyContentArr) => void;
    handleCloseSurvey: () => void;
    handleProgressBarClick: (index: number) => void;
    setOpenUsersModal: (index: number) => void;
};

const CSSQuickSurveyList = styled.ul`
    list-style: none;
`;

const CSSQuickSurveyListItem = styled.li`
    padding: ${(props) => props.theme.space.md}px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const JRCWidgetQuickSurvey = ({
    limit,
    values,
    userResponses,
    isClosed,
    canCloseSurvey,
    userHasAlreadyVoted,
    anonymize,
    seeResponseAfter,
    canUpdate,
    handleCloseSurvey,
    handleProgressBarClick,
    handleClickFile,
    setOpenUsersModal,
}: JRCWidgetQuickSurveyProps) => {
    const intl = useIntl();
    const [openModal, setOpenModal] = React.useState(false);

    if (!userResponses || userResponses?.length === 0) return null;
    const canSeeResponses = !seeResponseAfter || (seeResponseAfter && userHasAlreadyVoted);
    return (
        <>
            {isClosed && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography size={'s'} color="orange" weight="bold">
                        <FormattedMessage id="APP_SurveyDate_Over" />
                    </Typography>
                </div>
            )}
            <CSSQuickSurveyList>
                {values.map((value, index) => (
                    <CSSQuickSurveyListItem key={index}>
                        <Typography size={'m'}>{value.title}</Typography>
                        {value.file !== undefined && (
                            <JRCWidgetQuickSurveyImage
                                file={value.file}
                                onClick={() => handleClickFile && handleClickFile(value)}
                            />
                        )}
                        <JRCWidgetQuickSurveyResponse
                            anonymize={anonymize}
                            userResponses={userResponses[index] ?? { counter: 0, userList: [] }}
                            canSeeResponses={canSeeResponses}
                            limit={limit}
                            isClosed={isClosed}
                            setOpenUsersModal={() => setOpenUsersModal(index)}
                            handleResponseClick={() => {
                                if (!isClosed) {
                                    handleProgressBarClick(index);
                                }
                            }}
                        />
                    </CSSQuickSurveyListItem>
                ))}
            </CSSQuickSurveyList>
            {canCloseSurvey && !isClosed && canUpdate && (
                <JRCButton variant="link" onClick={() => setOpenModal(true)}>
                    <Typography size={'s'}>
                        <FormattedMessage id="WIDGET_Survey_Close_Survey" />
                    </Typography>
                </JRCButton>
            )}
            <JRCModal
                open={openModal}
                closeHandler={() => setOpenModal(false)}
                title={intl.formatMessage({ id: 'WIDGET_Survey_Close_Survey' })}
                buttons={[
                    {
                        color: 'cancel',
                        children: intl.formatMessage({ id: 'GLOBAL_Cancel' }),
                        clickHandler: () => {
                            setOpenModal(false);
                        },
                    },
                    {
                        children: intl.formatMessage({ id: 'GLOBAL_Confirm' }),
                        clickHandler: () => {
                            handleCloseSurvey();
                            setOpenModal(false);
                        },
                    },
                ]}
                enableClickAwayCloseModal={false}>
                <Typography size={'m'}>
                    {intl.formatMessage({ id: 'WIDGET_Survey_Close_Survey_Description' })}
                </Typography>
            </JRCModal>
        </>
    );
};
